import React from 'react';
import { useNavigate } from 'react-router-dom';

const C = {
  navy: "#0D2B45",
  blue: "#1a5fa8",
  sky: "#2d9de8",
  teal: "#0ab5a0",
  purple: "#5b3fa6",
  gold: "#e8a020",
  white: "#ffffff",
  offWhite: "#f7f9fc",
  slate: "#64748b",
  dark: "#0a1628",
  green: "#059669",
};

const fonts = {
  display: "'Playfair Display', Georgia, serif",
  body: "'DM Sans', system-ui, sans-serif",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:wght@300;400;500;600;700&display=swap');

  @keyframes fadeUp  { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
  @keyframes gradMove{ 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }

  .hm-fu  { animation:fadeUp .7s ease both; }
  .hm-fi  { animation:fadeIn .8s ease both; }
  .hm-hover { transition:transform .3s,box-shadow .3s; }
  .hm-hover:hover { transform:translateY(-6px); box-shadow:0 24px 56px rgba(13,43,69,.14)!important; }
  .hm-btn { cursor:pointer; border:none; font-family:'DM Sans',system-ui,sans-serif; font-weight:700; border-radius:10px; transition:all .2s; }
  .hm-btn:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(0,0,0,.18)!important; }
  .hm-step:hover .hm-step-num { transform:scale(1.1); }

  .hm-hero-section {
    position: relative;
    background: linear-gradient(160deg,#0a1628 0%,#0D2B45 55%,#1a2d4a 100%);
    overflow: hidden;
    padding: 100px 24px 80px;
    min-height: 92vh;
    display: flex;
    align-items: center;
  }
  .hm-hero-grid {
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 420px;
    gap: 64px;
    align-items: center;
    position: relative;
  }
  .hm-hero-btn-row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 40px;
  }
  .hm-trust-badges { display: flex; gap: 20px; flex-wrap: wrap; }
  .hm-disease-grid {
    max-width: 1100px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(3,1fr);
    border-bottom: 1px solid #f0f4f8;
  }
  .hm-education-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 24px; margin-bottom: 48px; }
  .hm-copilot-grid { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 72px; align-items: center; }
  .hm-copilot-chat { display: block; }
  .hm-warn-grid { display: grid; grid-template-columns: repeat(auto-fit,minmax(240px,1fr)); gap: 20px; }
  .hm-centers-grid { display: grid; grid-template-columns: repeat(auto-fit,minmax(200px,1fr)); gap: 16px; margin-bottom: 32px; }
  .hm-cta-btns { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
  .hm-section-pad { padding: 96px 24px; }

  @media (max-width: 768px) {
    .hm-hero-section { padding: 72px 16px 48px !important; min-height: auto !important; }
    .hm-hero-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
    .hm-hero-btn-row { flex-direction: column !important; }
    .hm-hero-btn-row .hm-btn { width: 100%; text-align: center; }
    .hm-trust-badges { flex-direction: column !important; gap: 8px !important; }
    .hm-disease-grid { grid-template-columns: 1fr !important; }
    .hm-disease-grid > div { border-right: none !important; border-bottom: 1px solid #f0f4f8; }
    .hm-education-grid { grid-template-columns: 1fr !important; }
    .hm-copilot-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
    .hm-copilot-chat { display: none !important; }
    .hm-section-pad { padding: 64px 16px !important; }
    .hm-warn-grid { grid-template-columns: 1fr 1fr !important; }
    .hm-centers-grid { grid-template-columns: 1fr 1fr !important; }
    .hm-cta-btns { flex-direction: column !important; align-items: center !important; }
    .hm-cta-btns .hm-btn { width: 100%; max-width: 320px; text-align: center; }
  }

  @media (max-width: 480px) {
    .hm-warn-grid { grid-template-columns: 1fr !important; }
    .hm-centers-grid { grid-template-columns: 1fr !important; }
  }
`;

function SL({ children, light = false }) {
  return (
    <p style={{ fontFamily: fonts.body, fontSize: 11, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: light ? "rgba(255,255,255,.5)" : "#2d9de8", margin: "0 0 12px" }}>
      {children}
    </p>
  );
}

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div style={{ fontFamily: fonts.body, background: "#f7f9fc", overflowX: "hidden" }}>
      <style>{STYLES}</style>

      {/* HERO */}
      <section className="hm-hero-section">
        <div style={{ position: "absolute", top: -100, right: -80, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,#2d9de81e,transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -80, left: -60, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle,#0ab5a018,transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "35%", left: "15%", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle,#5b3fa614,transparent 70%)", pointerEvents: "none" }} />

        <div className="hm-hero-grid">
          <div>
            <span className="hm-fu" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 16px", borderRadius: 999, background: "#0ab5a022", color: "#0ab5a0", border: "1px solid #0ab5a040", fontFamily: fonts.body, fontSize: 12, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 24, animationDelay: "0s" }}>
              🧬 AI-Powered · Free for Patients
            </span>

            <h1 className="hm-fu" style={{ fontFamily: fonts.display, fontSize: "clamp(2.2rem,5.5vw,4.2rem)", fontWeight: 900, color: "#ffffff", lineHeight: 1.1, margin: "0 0 24px", animationDelay: ".1s" }}>
              Find Your Path to<br />
              <span style={{ background: "linear-gradient(135deg,#2d9de8,#0ab5a0)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>Better Treatment</span><br />
              <span style={{ color: "rgba(255,255,255,.55)", fontSize: "75%", fontStyle: "italic" }}>Without the Confusion</span>
            </h1>

            <p className="hm-fu" style={{ fontSize: "clamp(1rem,1.8vw,1.2rem)", color: "rgba(255,255,255,.72)", lineHeight: 1.85, maxWidth: 600, margin: "0 0 36px", animationDelay: ".2s" }}>
              NephroTrax matches patients to clinical trials using AI — explains everything in plain English, and coordinates travel so you can focus on getting the care you need.
            </p>

            <div className="hm-fu hm-hero-btn-row" style={{ animationDelay: ".3s" }}>
              <button className="hm-btn" onClick={() => navigate('/trials/nlp-search')} style={{ padding: "15px 32px", background: "linear-gradient(135deg,#2d9de8,#0ab5a0)", color: "#ffffff", fontSize: 16, boxShadow: "0 8px 32px #2d9de844" }}>
                Check My Eligibility — Free →
              </button>
              <button className="hm-btn" onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })} style={{ padding: "15px 28px", background: "rgba(255,255,255,.1)", color: "#ffffff", border: "1px solid rgba(255,255,255,.3)", fontSize: 15, fontWeight: 600 }}>
                How It Works
              </button>
            </div>

            <div className="hm-fu" style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.15)", borderRadius: 14, padding: "20px 24px", maxWidth: 580, backdropFilter: "blur(10px)", animationDelay: ".4s", marginBottom: 28 }}>
              <p style={{ fontFamily: fonts.display, fontStyle: "italic", fontSize: 15, color: "rgba(255,255,255,.85)", margin: "0 0 8px", lineHeight: 1.7 }}>
                "I spent weeks trying to understand clinical trials on my own. NephroTrax made it simple — I found a trial and contacted the investigator in under 5 minutes."
              </p>
              <p style={{ fontFamily: fonts.body, fontSize: 12, color: "rgba(255,255,255,.45)", margin: 0 }}>— Sarah M., AL Amyloidosis patient</p>
            </div>

            <div className="hm-fu hm-trust-badges" style={{ animationDelay: ".5s" }}>
              {[["✅", "100+ patients nationwide"], ["✅", "Built by 20-yr healthcare IT veteran"], ["✅", "Free to use, always"]].map(([ic, label], i) => (
                <span key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: fonts.body, fontSize: 13, color: "rgba(255,255,255,.65)" }}>
                  {ic} {label}
                </span>
              ))}
            </div>
          </div>

          {/* Right cards */}
          <div className="hm-fu" style={{ animationDelay: ".3s", display: "flex", flexDirection: "column", gap: 14 }}>
            <div onClick={() => navigate("/my-health/find-provider")}
              style={{ background: "#ffffff", borderRadius: 20, overflow: "hidden", cursor: "pointer", boxShadow: "0 24px 64px rgba(0,0,0,0.3)", transition: "all 0.25s" }}
              onMouseOver={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 32px 80px rgba(0,0,0,0.4)"; }}
              onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 24px 64px rgba(0,0,0,0.3)"; }}>
              <div style={{ height: 3, background: "linear-gradient(90deg,#2d9de8,#0ab5a0)" }} />
              <div style={{ overflow: "hidden", height: 180 }}>
                <img src={require("../assets/find-provider-preview.png")} alt="Map showing Dr. Nitasha Sarswat Amyloidosis Specialist"
                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block" }} />
              </div>
              <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <p style={{ fontFamily: fonts.body, fontSize: 10, fontWeight: 700, color: "#0ab5a0", textTransform: "uppercase", letterSpacing: ".08em", margin: "0 0 4px" }}>300,000+ physicians</p>
                  <h3 style={{ fontFamily: fonts.display, fontSize: 15, fontWeight: 700, color: "#0D2B45", margin: 0, lineHeight: 1.3 }}>Find an Amyloidosis Specialist Near You</h3>
                </div>
                <button className="hm-btn" style={{ padding: "10px 16px", fontSize: 12, whiteSpace: "nowrap", flexShrink: 0, background: "linear-gradient(135deg,#2d9de8,#0ab5a0)", color: "#ffffff", boxShadow: "0 4px 16px #2d9de844" }}>
                  Find a Specialist →
                </button>
              </div>
            </div>

            <div onClick={() => navigate("/amyloid/clinicaltrialsearch")}
              style={{ background: "#ffffff", borderRadius: 20, overflow: "hidden", cursor: "pointer", boxShadow: "0 24px 64px rgba(0,0,0,0.3)", transition: "all 0.25s" }}
              onMouseOver={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 32px 80px rgba(0,0,0,0.4)"; }}
              onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 24px 64px rgba(0,0,0,0.3)"; }}>
              <div style={{ height: 3, background: "linear-gradient(90deg,#0ab5a0,#5b3fa6)" }} />
              <div style={{ overflow: "hidden", height: 180 }}>
                <img src={require("../assets/trial-translation-preview.png")} alt="Trial eligibility plain English translation"
                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top left", display: "block" }} />
              </div>
              <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <p style={{ fontFamily: fonts.body, fontSize: 10, fontWeight: 700, color: "#0ab5a0", textTransform: "uppercase", letterSpacing: ".08em", margin: "0 0 4px" }}>AI-powered</p>
                  <h3 style={{ fontFamily: fonts.display, fontSize: 15, fontWeight: 700, color: "#0D2B45", margin: 0, lineHeight: 1.3 }}>Understand Trial Eligibility in Plain English</h3>
                </div>
                <button className="hm-btn" style={{ padding: "10px 16px", fontSize: 12, whiteSpace: "nowrap", flexShrink: 0, background: "linear-gradient(135deg,#2d9de8,#0ab5a0)", color: "#ffffff", boxShadow: "0 4px 16px #2d9de844" }}>
                  Search Trials →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DISEASE TYPES BAND */}
      <section style={{ background: "#ffffff", padding: "0 24px" }}>
        <div className="hm-disease-grid">
          {[
            { type: "🧪 AL Amyloidosis", sub: "Light Chain", tag: "Most common", color: "#1a5fa8", body: "Caused by abnormal plasma cells. Affects heart, kidneys, liver, and nerves. Treated with chemotherapy and stem cell transplant." },
            { type: "💜 ATTR Amyloidosis", sub: "Transthyretin", tag: "FDA-approved treatments available", color: "#0ab5a0", body: "Caused by unstable transthyretin protein. Primarily affects the heart (ATTR-CM) and nerves (ATTR-PN). Tafamidis and acoramidis now approved." },
            { type: "🔬 AA Amyloidosis", sub: "Serum Amyloid A", tag: "Secondary amyloidosis", color: "#5b3fa6", body: "Caused by chronic inflammation. Primarily affects the kidneys. Treating the underlying inflammatory condition is key." },
          ].map((d, i) => (
            <div key={i} style={{ padding: "40px 32px", borderRight: i < 2 ? "1px solid #f0f4f8" : "none" }}>
              <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontFamily: fonts.body, fontSize: 11, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", background: `${d.color}14`, color: d.color, marginBottom: 12 }}>{d.tag}</span>
              <h3 style={{ fontFamily: fonts.display, fontSize: 18, fontWeight: 700, color: "#0D2B45", margin: "0 0 4px" }}>{d.type}</h3>
              <p style={{ fontFamily: fonts.body, fontSize: 12, color: d.color, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", margin: "0 0 12px" }}>{d.sub}</p>
              <p style={{ fontFamily: fonts.body, fontSize: 14, color: "#64748b", lineHeight: 1.7, margin: 0 }}>{d.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="hm-section-pad" style={{ background: "#f7f9fc" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <SL>Everything a Patient Needs</SL>
            <h2 style={{ fontFamily: fonts.display, fontSize: "clamp(1.8rem,3.5vw,2.8rem)", fontWeight: 800, color: "#0D2B45", margin: "0 auto 16px", maxWidth: 560 }}>
              From Diagnosis to Treatment — We're With You
            </h2>
            <p style={{ fontFamily: fonts.body, fontSize: 16, color: "#64748b", maxWidth: 580, margin: "0 auto", lineHeight: 1.8 }}>
              One free platform that handles everything: finding trials, understanding your disease, coordinating care, and getting to your appointments.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 24 }}>
            {[
              { icon: "🎯", title: "AI Trial Matching", body: "Enter your amyloid type, organ involvement, and treatment history. Our AI finds the right trials for you in seconds — no medical jargon required.", cta: "Find My Trials", path: "/trials/smart-match", color: "#1a5fa8" },
              { icon: "✈️", title: "Travel Coordination", body: "Book flights, hotels, and ground transportation to your trial site — our integrated travel platform built specifically for clinical trial patients.", cta: "Explore Travel", path: "/tools/travel", color: "#0ab5a0" },
              { icon: "📚", title: "Disease Education", body: "Comprehensive resources on AL, ATTR, and AA amyloidosis — symptoms, diagnosis, treatment options, and what to ask your doctor. In plain English.", cta: "Start Learning", path: "/amyloid", color: "#5b3fa6" },
              { icon: "🤝", title: "Care Copilot AI", body: "Your personal AI health companion. Ask anything about amyloidosis, get your lab results interpreted, and prepare for doctor appointments with confidence.", cta: "Try Care Copilot", path: "/my-health/care-copilot", color: "#e8a020" },
              { icon: "🏥", title: "Specialist Finder", body: "Find amyloidosis centers of excellence near you — UChicago Medicine, Mayo Clinic, Stanford, and more. See a specialist who sees this every day.", cta: "Find a Specialist", path: "/tools/second-opinion", color: "#e05c3a" },
              { icon: "🔒", title: "HIPAA Compliant", body: "Your health data is protected with enterprise-grade AWS security. We never sell your information and you control everything you share.", cta: "Learn More", path: "/about", color: "#059669" },
            ].map((f, i) => (
              <div key={i} className="hm-hover" style={{ background: "#ffffff", borderRadius: 20, padding: "32px 28px", boxShadow: "0 4px 20px rgba(13,43,69,.07)", borderTop: `3px solid ${f.color}`, display: "flex", flexDirection: "column", animation: `fadeUp .6s ${i * .08}s ease both` }}>
                <div style={{ fontSize: 36, marginBottom: 14 }}>{f.icon}</div>
                <h3 style={{ fontFamily: fonts.display, fontSize: 19, fontWeight: 700, color: "#0D2B45", margin: "0 0 10px" }}>{f.title}</h3>
                <p style={{ fontFamily: fonts.body, fontSize: 14, color: "#64748b", lineHeight: 1.7, margin: "0 0 auto", flex: 1 }}>{f.body}</p>
                <button className="hm-btn" onClick={() => navigate(f.path)} style={{ marginTop: 20, padding: "10px 0", width: "100%", background: `${f.color}14`, color: f.color, border: `1px solid ${f.color}30`, fontSize: 13 }}>
                  {f.cta} →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="hm-section-pad" style={{ background: "#ffffff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <SL>Simple Process</SL>
            <h2 style={{ fontFamily: fonts.display, fontSize: "clamp(1.8rem,3.5vw,2.8rem)", fontWeight: 800, color: "#0D2B45", margin: "0 auto 16px", maxWidth: 520 }}>
              From Diagnosis to Trial in Minutes
            </h2>
            <p style={{ fontFamily: fonts.body, fontSize: 16, color: "#64748b", maxWidth: 540, margin: "0 auto", lineHeight: 1.8 }}>
              We've simplified the clinical trial discovery process so patients can focus on what matters — getting better.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 32 }}>
            {[
              { n: "01", title: "Create Your Profile", body: "Enter your amyloid type, organ involvement, and treatment history. Takes about 5 minutes.", icon: "👤", color: "#1a5fa8" },
              { n: "02", title: "Get Matched", body: "Our AI instantly finds clinical trials that fit your specific situation — filtered by eligibility and location.", icon: "🎯", color: "#0ab5a0" },
              { n: "03", title: "Review & Compare", body: "Compare trials side by side — eligibility criteria, visit schedules, compensation, and site locations.", icon: "📋", color: "#5b3fa6" },
              { n: "04", title: "Book Travel", body: "Coordinate flights, hotels, and ground transportation to your trial site — all in one place.", icon: "✈️", color: "#e8a020" },
            ].map((s, i) => (
              <div key={i} className="hm-step" style={{ textAlign: "center", padding: "32px 20px", animation: `fadeUp .6s ${i * .12}s ease both` }}>
                <div className="hm-step-num" style={{ width: 80, height: 80, borderRadius: "50%", background: `linear-gradient(135deg,${s.color},${s.color}bb)`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: "1.8rem", boxShadow: `0 8px 24px ${s.color}40`, transition: "transform .3s" }}>
                  {s.icon}
                </div>
                <div style={{ fontFamily: fonts.body, fontSize: 11, fontWeight: 700, color: s.color, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 8 }}>{s.n}</div>
                <h3 style={{ fontFamily: fonts.display, fontSize: 19, fontWeight: 700, color: "#0D2B45", margin: "0 0 10px" }}>{s.title}</h3>
                <p style={{ fontFamily: fonts.body, fontSize: 14, color: "#64748b", lineHeight: 1.7, margin: 0 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PATIENT EDUCATION */}
      <section className="hm-section-pad" style={{ background: "#f7f9fc" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <SL>Understanding Your Type Matters</SL>
            <h2 style={{ fontFamily: fonts.display, fontSize: "clamp(1.8rem,3.5vw,2.6rem)", fontWeight: 800, color: "#0D2B45", margin: "0 auto 16px", maxWidth: 560 }}>
              Knowing Your Type Changes Everything
            </h2>
            <p style={{ fontFamily: fonts.body, fontSize: 16, color: "#64748b", maxWidth: 540, margin: "0 auto", lineHeight: 1.8 }}>
              Amyloidosis has multiple types — and the treatment is completely different for each. Getting the right diagnosis from day one is critical.
            </p>
          </div>
          <div className="hm-education-grid">
            {[
              { t: "AL Amyloidosis", sub: "Light Chain", facts: ["Caused by abnormal bone marrow plasma cells", "Affects heart, kidneys, liver, nerves", "Not inherited — affects adults of any age", "Treated with chemotherapy + stem cell transplant", "Fast progression — early diagnosis critical"], color: "#1a5fa8", icon: "🧪" },
              { t: "ATTR Amyloidosis", sub: "Transthyretin", facts: ["Wild-type (age-related) or hereditary", "Primarily affects heart and nerves", "FDA-approved: tafamidis, acoramidis, eplontersen", "V122I variant common in African Americans", "New treatments rapidly expanding"], color: "#0ab5a0", icon: "💜" },
              { t: "AA Amyloidosis", sub: "Serum Amyloid A", facts: ["Caused by chronic inflammatory disease", "Primarily affects the kidneys", "Linked to rheumatoid arthritis, IBD, and others", "Treat the underlying inflammation first", "SAA levels guide treatment response"], color: "#5b3fa6", icon: "🔬" },
            ].map((d, i) => (
              <div key={i} className="hm-hover" style={{ background: "#ffffff", borderRadius: 20, padding: "32px 28px", boxShadow: "0 4px 20px rgba(13,43,69,.07)", borderLeft: `4px solid ${d.color}`, animation: `fadeUp .6s ${i * .1}s ease both` }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>{d.icon}</div>
                <h3 style={{ fontFamily: fonts.display, fontSize: 18, fontWeight: 700, color: "#0D2B45", margin: "0 0 4px" }}>{d.t}</h3>
                <p style={{ fontFamily: fonts.body, fontSize: 11, color: d.color, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", margin: "0 0 16px" }}>{d.sub}</p>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {d.facts.map((f, j) => (
                    <li key={j} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8, fontFamily: fonts.body, fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
                      <span style={{ color: d.color, flexShrink: 0, fontWeight: 700 }}>·</span>{f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center" }}>
            <button className="hm-btn" onClick={() => navigate('/amyloid')} style={{ padding: "14px 32px", background: "linear-gradient(135deg,#1a5fa8,#2d9de8)", color: "#ffffff", fontSize: 15, boxShadow: "0 6px 24px #2d9de844" }}>
              Learn About Your Type →
            </button>
          </div>
        </div>
      </section>

      {/* CARE COPILOT */}
      <section className="hm-section-pad" style={{ background: "linear-gradient(160deg,#0a1628,#0D2B45,#1a2d4a)", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "10%", right: "5%", width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle,#5b3fa618,transparent 70%)", pointerEvents: "none" }} />
        <div className="hm-copilot-grid">
          <div>
            <SL light>AI-Powered</SL>
            <h2 style={{ fontFamily: fonts.display, fontSize: "clamp(1.8rem,3.5vw,2.6rem)", fontWeight: 800, color: "#ffffff", margin: "0 0 24px", lineHeight: 1.2 }}>
              Care Copilot — Your Personal Amyloidosis AI
            </h2>
            <p style={{ fontFamily: fonts.body, fontSize: 16, color: "rgba(255,255,255,.7)", lineHeight: 1.9, margin: "0 0 16px" }}>
              Powered by Claude Sonnet on Amazon Bedrock. Ask anything about amyloidosis in plain English, understand your clinical trials, and get your lab results interpreted — NT-proBNP, free light chains, eGFR, troponin — without needing a medical degree.
            </p>
            <p style={{ fontFamily: fonts.body, fontSize: 16, color: "rgba(255,255,255,.7)", lineHeight: 1.9, margin: "0 0 32px" }}>
              Premium members can upload PDF lab results for AI-powered interpretation. Free tier includes 3 questions to get started.
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 32 }}>
              {[["💬", "Ask anything about amyloidosis"], ["🧪", "Upload & interpret lab results"], ["🎯", "Profile-aware answers"], ["🏥", "Find the right specialist"]].map(([ic, l], i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, background: "rgba(255,255,255,.08)", color: "rgba(255,255,255,.8)", border: "1px solid rgba(255,255,255,.15)", fontFamily: fonts.body, fontSize: 12, fontWeight: 600 }}>
                  {ic} {l}
                </span>
              ))}
            </div>
            <button className="hm-btn" onClick={() => navigate('/my-health/care-copilot')} style={{ padding: "14px 32px", background: "linear-gradient(135deg,#5b3fa6,#7c3aed)", color: "#ffffff", fontSize: 15, boxShadow: "0 8px 28px #5b3fa655" }}>
              Try Care Copilot — 3 Free Questions →
            </button>
          </div>

          {/* Mock chat — hidden on mobile */}
          <div className="hm-copilot-chat" style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 20, overflow: "hidden", backdropFilter: "blur(20px)" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,.08)", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#059669" }} />
              <span style={{ fontFamily: fonts.body, fontSize: 13, color: "rgba(255,255,255,.55)" }}>Care Copilot · by NephroTrax</span>
            </div>
            <div style={{ padding: "24px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
              {[
                { role: "ai", text: "Hi! I'm Care Copilot. I can answer questions about amyloidosis, explain your clinical trials, and help you interpret lab results. What would you like to know?" },
                { role: "user", text: "What does my NT-proBNP level of 1800 mean for ATTR-CM?" },
                { role: "ai", text: "An NT-proBNP of 1800 pg/mL in ATTR-CM indicates moderate cardiac stress. This level corresponds roughly to Mayo Stage II disease. Your cardiologist will track this number over time — it's one of the key markers used to assess disease progression and treatment response with tafamidis or acoramidis." },
              ].map((m, i) => (
                <div key={i} style={{ display: "flex", gap: 10, flexDirection: m.role === "user" ? "row-reverse" : "row" }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, background: m.role === "user" ? "linear-gradient(135deg,#5b3fa6,#7c3aed)" : "rgba(255,255,255,.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{m.role === "user" ? "👤" : "🤝"}</div>
                  <div style={{ maxWidth: "75%", padding: "12px 16px", borderRadius: m.role === "user" ? "16px 4px 16px 16px" : "4px 16px 16px 16px", background: m.role === "user" ? "linear-gradient(135deg,#5b3fa6,#7c3aed)" : "rgba(255,255,255,.08)", fontSize: 13, lineHeight: 1.65, color: m.role === "user" ? "white" : "rgba(255,255,255,.88)", border: m.role !== "user" ? "1px solid rgba(255,255,255,.1)" : "none" }}>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: "12px 16px 16px", borderTop: "1px solid rgba(255,255,255,.08)", display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["AL vs ATTR?", "What is NT-proBNP?", "Why no matched trials?"].map((s, i) => (
                <span key={i} style={{ padding: "5px 12px", borderRadius: 999, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.12)", fontFamily: fonts.body, fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,.6)", cursor: "pointer" }}>
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WARNING SIGNS */}
      <section className="hm-section-pad" style={{ background: "#ffffff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <SL>Early Detection Saves Lives</SL>
            <h2 style={{ fontFamily: fonts.display, fontSize: "clamp(1.6rem,3vw,2.4rem)", fontWeight: 800, color: "#0D2B45", margin: "0 auto 16px", maxWidth: 520 }}>
              Symptoms Often Mistaken for Other Conditions
            </h2>
            <p style={{ fontFamily: fonts.body, fontSize: 15, color: "#64748b", maxWidth: 520, margin: "0 auto", lineHeight: 1.8 }}>
              Amyloidosis is diagnosed on average 2–3 years after symptoms begin. Recognizing the pattern early saves lives.
            </p>
          </div>
          <div className="hm-warn-grid">
            {[
              { icon: "❤️", title: "Cardiac", body: "Shortness of breath, swelling in legs and ankles, irregular heartbeat, fatigue with minimal activity.", color: "#1a5fa8" },
              { icon: "🫁", title: "Renal", body: "Foamy urine, swollen ankles, protein leakage — kidney amyloidosis often presents as nephrotic syndrome.", color: "#0ab5a0" },
              { icon: "🧠", title: "Neurological", body: "Numbness and tingling in hands and feet, carpal tunnel syndrome, digestive issues, dizziness upon standing.", color: "#5b3fa6" },
              { icon: "⚡", title: "Systemic", body: "Extreme fatigue, unintentional weight loss, enlarged tongue (macroglossia), periorbital bruising.", color: "#e8a020" },
            ].map((w, i) => (
              <div key={i} className="hm-hover" style={{ background: "#f7f9fc", borderRadius: 16, padding: "28px 24px", borderTop: `3px solid ${w.color}`, animation: `fadeUp .6s ${i * .08}s ease both` }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>{w.icon}</div>
                <h4 style={{ fontFamily: fonts.display, fontSize: 17, fontWeight: 700, color: "#0D2B45", margin: "0 0 10px" }}>{w.title} Warning Signs</h4>
                <p style={{ fontFamily: fonts.body, fontSize: 14, color: "#64748b", lineHeight: 1.7, margin: 0 }}>{w.body}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <button className="hm-btn" onClick={() => navigate('/amyloid')} style={{ padding: "12px 28px", background: "#0D2B4514", color: "#0D2B45", border: "1px solid #0D2B4525", fontSize: 14 }}>
              Learn More About Diagnosis →
            </button>
          </div>
        </div>
      </section>

      {/* CENTERS OF EXCELLENCE */}
      <section className="hm-section-pad" style={{ background: "#f7f9fc" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
          <SL>Centers of Excellence</SL>
          <h2 style={{ fontFamily: fonts.display, fontSize: "clamp(1.6rem,3vw,2.2rem)", fontWeight: 800, color: "#0D2B45", margin: "0 auto 16px", maxWidth: 480 }}>
            See a Specialist Who Knows Amyloidosis
          </h2>
          <p style={{ fontFamily: fonts.body, fontSize: 15, color: "#64748b", maxWidth: 520, margin: "0 auto 40px", lineHeight: 1.8 }}>
            General hospitals often miss amyloidosis. Specialized centers have teams who see amyloidosis patients every day.
          </p>
          <div className="hm-centers-grid">
            {[
              { name: "University of Chicago Medicine", sub: "Dr. Nitasha Sarswat · Chicago, IL", icon: "🏥" },
              { name: "Brigham and Women's Hospital", sub: "Dr. Sarah Cuddy · Boston, MA", icon: "🏥" },
              { name: "Mayo Clinic", sub: "Dr. Julie Rosenthal · Rochester, MN", icon: "🏥" },
              { name: "Stanford Health Care", sub: "Dr. Ronald Witteles · Stanford, CA", icon: "🏥" },
            ].map((c, i) => (
              <div key={i} className="hm-hover" style={{ background: "#ffffff", borderRadius: 16, padding: "24px 20px", boxShadow: "0 2px 16px rgba(13,43,69,.07)", textAlign: "left" }}>
                <div style={{ fontSize: 24, marginBottom: 10 }}>{c.icon}</div>
                <h4 style={{ fontFamily: fonts.display, fontSize: 15, fontWeight: 700, color: "#0D2B45", margin: "0 0 6px" }}>{c.name}</h4>
                <p style={{ fontFamily: fonts.body, fontSize: 12, color: "#64748b", margin: 0 }}>{c.sub}</p>
              </div>
            ))}
          </div>
          <button className="hm-btn" onClick={() => navigate('/tools/second-opinion')} style={{ padding: "12px 28px", background: "linear-gradient(135deg,#1a5fa8,#2d9de8)", color: "#ffffff", fontSize: 14, boxShadow: "0 4px 16px #2d9de844" }}>
            Find a Specialist Near You →
          </button>
        </div>
      </section>

      {/* MISSION CTA */}
      <section className="hm-section-pad" style={{ background: "linear-gradient(135deg,#1a5fa8,#2d9de8,#0ab5a0)", backgroundSize: "200% 200%", animation: "gradMove 8s ease infinite", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "-30%", left: "30%", width: 400, height: 400, borderRadius: "50%", background: "rgba(255,255,255,.06)", pointerEvents: "none" }} />
        <div style={{ maxWidth: 800, margin: "0 auto", position: "relative" }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: "clamp(1.8rem,4vw,3rem)", fontWeight: 900, color: "#ffffff", margin: "0 0 20px", lineHeight: 1.2 }}>
            Built to Give Every Patient a Fighting Chance
          </h2>
          <p style={{ fontFamily: fonts.body, fontSize: 17, color: "rgba(255,255,255,.85)", lineHeight: 1.9, margin: "0 0 16px", maxWidth: 660, marginLeft: "auto", marginRight: "auto" }}>
            NephroTrax exists because too many amyloidosis patients never find the clinical trials that could save their lives — not because the trials don't exist, but because the path to finding them is broken.
          </p>
          <p style={{ fontFamily: fonts.display, fontStyle: "italic", fontSize: "clamp(1.1rem,2vw,1.4rem)", color: "rgba(255,255,255,.9)", margin: "0 0 48px" }}>
            NephroTrax is free for every patient. Always.
          </p>
          <div className="hm-cta-btns">
            <button className="hm-btn" onClick={() => navigate('/register')} style={{ padding: "16px 40px", background: "#ffffff", color: "#1a5fa8", fontSize: 16, boxShadow: "0 8px 32px rgba(0,0,0,.2)" }}>
              Get Started — Free →
            </button>
            <button className="hm-btn" onClick={() => navigate('/about')} style={{ padding: "16px 32px", background: "rgba(255,255,255,.15)", color: "#ffffff", border: "1px solid rgba(255,255,255,.4)", fontSize: 15, fontWeight: 600 }}>
              Our Mission
            </button>
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <div style={{ background: "#f0f4f8", padding: "20px 24px", textAlign: "center" }}>
        <p style={{ fontFamily: fonts.body, fontSize: 12, color: "#64748b", maxWidth: 900, margin: "0 auto", lineHeight: 1.7 }}>
          ⚕️ <strong>Medical Disclaimer:</strong> NephroTrax is an informational platform and does not provide medical advice, diagnosis, or treatment. Always consult with qualified healthcare providers regarding your medical condition and treatment options. Clinical trial participation decisions should be made in consultation with your healthcare team.
        </p>
      </div>
    </div>
  );
};

export default HomePage;